import * as z from 'zod';
import { Form, TextInput } from 'components';
import { Controller } from 'react-hook-form';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  makeStyles,
  Box,
  Button,
  CircularProgress,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import { useState } from 'react';
import { useAuth } from 'context/authContext';
import { LoginFormValues } from '../types';

interface LoginFormProps {
  onSuccess: () => void;
}

const formSchema = z.object({
  login: z.string().nonempty('O login obrigatório.'),
  password: z
    .string()
    .min(6, 'Mínimo de seis digitos.')
    .nonempty('A senha é obrigatória.'),
  rememberMe: z.boolean().optional(),
});

const useStyles = makeStyles(theme => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  wrapper: {
    display: 'flex',
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    margin: theme.spacing(1),
  },
  buttonProgress: {
    color: theme.palette.info.contrastText,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -10,
    marginLeft: -15,
  },
  buttonSuccess: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.main,
    },
  },
}));

export const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const classes = useStyles();
  const { SignIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  function togglePassword() {
    setShowPassword(prev => !prev);
  }

  const onSubmit = async (data: LoginFormValues) => {
    try {
      await SignIn(data);
      onSuccess();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Form<LoginFormValues, typeof formSchema>
      onSubmit={onSubmit}
      schema={formSchema}
      className={classes.form}
      options={{
        defaultValues: {
          password: '',
          rememberMe: false,
        },
      }}
    >
      {({ register, formState: { errors, isSubmitting }, control }) => (
        <>
          <TextInput
            required
            registration={register('login')}
            name="login"
            label="Login"
            error={!!errors.login}
            errorMessage={errors.login?.message}
          />

          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <FormControl variant="outlined" fullWidth>
                <InputLabel htmlFor="outlined-adornment-password">
                  Senha
                </InputLabel>
                <OutlinedInput
                  required
                  id="outlined-adornment-password"
                  type={showPassword ? 'text' : 'password'}
                  error={!!errors.password}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePassword}
                        onMouseDown={e => e.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                  {...field}
                />
                {errors.password && (
                  <FormHelperText error>
                    {errors.password?.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Controller
            name="rememberMe"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size="small" color="primary" {...field} />}
                label="Lembrar de mim"
              />
            )}
          />
          <Box className={classes.wrapper}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? '...' : 'Entrar'}
            </Button>
            {isSubmitting && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </Box>
        </>
      )}
    </Form>
  );
};
